<template>
  <div id="SegmentationBulkEdit">
    <BulkEditor
      :columns="columns"
      :source="segments"
      :validate-row="validateRow"
      :loading="loading"
      :resource-name="{
        singular: $t('campaignEdit.bulkEdit.segment'),
        plural: $t('campaignEdit.bulkEdit.segments')  
      }"
      allow-delete
      :allow-copy="false"
      :allow-create="false"
      @view-row="viewRow"
      @commit="saveSegmentChanges"
      @discard="onDiscard"
    >
      <template v-slot:col:type="{value, item}">
        {{item.label}}
      </template>
      <template v-slot:col:tag="{value}">
        {{value ? value : '—'}}
      </template>
      <template v-slot:col:status="{value, item}">
        {{item.label}}
      </template>
      <template v-slot:col:offerIds="{value, item}">
        #{{item.id}} - {{item.displayText}} - {{item.promoCode}}
      </template>
      <template v-slot:col:widgetId="{value, item}">
        {{item ? item.name : '—'}}
      </template>
    </BulkEditor>
    <!-- <pre>Committed Changes: {{changes}}</pre> -->
    <!-- <pre>Segments: {{segments}}</pre> -->
    <!-- <pre>OfferMap: {{offerMap}}</pre> -->
    <!-- <pre>WidgetMap: {{widgetMap}}</pre> -->
  </div>
</template>

<script>
import OffersUtil from '../classes/utils/OffersUtil'

export default {
  name: 'SegmentationBulkEdit',
  props: {
    campaign: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      // offerMap: mapBy(offers, 'id'),
      // widgetMap: mapBy(widgets, 'id'),
      offers: [],
      offerMap: null,
      widgetMap: null,
      widgets: [],
      segments: [],
      loading: true
    }
  },
  mounted() {
    // console.log(this.$route.query)
    const segmentIds = this.$route.query.id || []
    Promise.all([
      this.api.getSegmentsByIds(segmentIds, this.campaign.id),
      this.api.getWidgetsForSegmentation(),
      this.api.getIncentives(this.campaign.id),
    ])
      .then((response) => {
        const [segments, widgets, offers] = response
        this.segments = segments || []
        this.widgets = this.formatWidgets(widgets) || []
        this.offers = OffersUtil.fillForFrontend(offers) || []
        this.offerMap = mapBy(this.offers, 'id')
        this.widgetMap = mapBy(this.widgets, 'id')
      })
      .catch(this.apiCatch)
      .finally(() => {
        this.loading = false
      })
  },
  computed: {
    columns() {
      return [
        {
          key: 'name',
          label: this.$t('Name'),
          type: 'text',
          defaultValue: this.$t('campaignEdit.bulkEdit.New segment'),
          width: 'minmax(150px, 2fr)'
        },
        {
          key: 'type',
          label: this.$t('Type'),
          type: 'list',
          options: [
            {label: this.$t('campaignEdit.segment.type.standard'), value: 'include'},
            {label: this.$t('campaignEdit.segment.type.exitIntent'), value: 'exit-intent'},
            {label: this.$t('campaignEdit.segment.type.hideOffers'), value: 'display-none'},
            {label: this.$t('campaignEdit.segment.type.doNotTarget'), value: 'exclude'},
            {label: this.$t('campaignEdit.segment.type.ignoreUser'), value: 'ignore-user'}
          ]
        },
        {
          key: 'status',
          label: this.$t('Status'),
          type: 'list',
          options: [
            {value: 'active', label: this.$t('Active')},
            {value: 'inactive', label: this.$t('Disabled')}
          ]
        },
        {
          key: 'offerIds',
          label: this.$t('Offers'),
          type: 'list',
          multiple: true,
          options: this.offers.map(d => ({
            value: d.id,
            label: d.label,
            description: d.description
          })),
          accessor: (id) => this.offerMap[id],
          // defaultValue: () => [13591],
          width: 'minmax(250px, 2fr)'
        },
        {
          key: 'widgetId',
          label: this.$t('Widget'),
          type: 'list',
          options: this.widgets.map(d => ({
            value: d.id,
            label: d.name,
            description: d.type ? this.$t(`creative.${d.type}`) : null
          })),
          accessor: (id) => this.widgetMap[id],
          width: 'minmax(150px, 2fr)'
        },
        {
          key: 'tag',
          label: this.$t('Tag'),
          type: 'text'
        },
        {
          key: 'overrideGenius',
          label: this.$t('Override Algorithms'),
          type: 'boolean'
        },
        {
          key: 'overrideDnt',
          label: this.$t('Overrride DNT'),
          type: 'boolean'
        },
        {
          key: 'isUpsell',
          label: this.$t('Upsell'),
          type: 'boolean'
        },
        {
          key: 'ignoreUserDays',
          label: this.$t('Ignore Days'),
          type: 'number',
          format: 'integer',
          min: 1
        }
      ]
    },
  },
  methods: {
    offersVisible(segment) {
      return segment.type !== 'exclude' &&
        segment.type !== 'display-none' &&
        segment.type !== 'ignore-user'
    },
    validateRow(row) {
      var widget = this.widgetMap[row.widgetId]
      if (row.overrideGenius && row.isUpsell) {
        return {
          colKey: 'isUpsell',
          message: this.$t('campaignEdit.bulkEdit.overrideUpsellError')
        }
      }
      if (row.isUpsell && (!row.offerIds || !row.offerIds.length)) {
        return {
          colKey: 'isUpsell',
          message: this.$t('campaignEdit.bulkEdit.upsellOfferError')
        }
      }
      if (row.type === 'exit-intent') {
        if (!row.offerIds || !row.offerIds.length) {
          return {
            colKey: 'offerIds',
            message: this.$t('campaignEdit.bulkEdit.exitIntentOfferError')
          }
        }
        if (widget && widget.value === -1)  {
          return {
            colKey: 'type',
            message: this.$t('campaignEdit.bulkEdit.exitIntentWidgetError')
          }
        }
      }
      else if (row.type === 'ignore-user') {
        if (row.ignoreUserDays <= 0) {
          return {
            colKey: 'ignoreUserDays',
            message: this.$t('campaignEdit.bulkEdit.ignoreUserDaysError')
          }
        }
      }
      
      if (!this.offersVisible(row)) {
        if (widget) {
          return {
            colKey: 'widgetId',
            message: this.$t('campaignEdit.bulkEdit.widgetSegmentTypeError')
          }
        } else if (row.offerIds && row.offerIds.length) {
          return {
            colKey: 'offerIds',
            message: this.$t('campaignEdit.bulkEdit.offerSegmentTypeError')
          }
        }
      } else if (this.offersVisible(row)) {
        if (this.widgets && this.widgets.length && !widget) {
          return {
            colKey: 'widgetId',
            message: this.$t('campaignEdit.error.setWidget')
          }
        }
      }
    },
    viewRow(id) {
      let routeData = this.$router.resolve(`/campaigns/${this.campaign.id}/segmentation/segment/${id}`);
      window.open(routeData.href, '_blank');
    },
    saveSegmentChanges(changes) {
      this.loading = true
      this.api.saveBulkSegments(changes)
        .then(() => {
          this.$ui.toast({ title: this.$t('Saved') })
          this.$router.push(`/campaigns/${this.campaign.id}/segmentation/list`)
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.loading = false
        })
    },
    onDiscard() {
      this.$router.push(`/campaigns/${this.campaign.id}/segmentation/list`)
    },
    formatWidgets(widgets) {
      let w = widgets.filter(w => {
        return w.type !== 'Smart'
      })
      w.unshift({
        name: `- ${this.$t('Use Default')} -`,
        id: -1
      })
      return w
    }
  }
}

function mapBy(array, prop) {
  return array.reduce((map, d) => {
    map[d[prop]] = d
    return map
  }, {})
}

</script>

<style>
.pill {
  padding: 2px 5px;
  border-radius: 3px;
  background: #e1e1e2;
  white-space: nowrap;
  overflow: hidden;
}

.pill +.pill {
  margin-top: 3px;
}
</style>